import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Tooltip, Input, Modal, message, Typography, Badge, Empty, Result,
  Space, Dropdown, Card, Row, Col, Segmented, Select, Spin, Collapse, Progress,
} from 'antd';
import { SearchOutlined, ReloadOutlined, FileTextOutlined, FileTextFilled, CopyOutlined, ShopOutlined, ArrowRightOutlined, ArrowLeftOutlined, InfoCircleOutlined, CheckCircleFilled, CloseCircleFilled, ThunderboltOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getRecTransmisiones,
  getRecTransmisionById,
  updateRecTransmisionEstado,
  procesarTransmision,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  INCIDENCIA_TOOLTIPS,
  INCIDENCIA_COLORS,
  getIncidencias,
  type ReconciliacionTransmision,
  type ReconciliacionIncidencia,
  type ProcesarTransmisionResponse,
} from '../../api/reconciliacionApi';
import FarmaciaTitularesModal from '../FarmaciaTitularesModal';
import AbrirCasoButton from '../MejoraCU2/AbrirCasoButton';

const { Text } = Typography;
const { TextArea } = Input;

const CRM_CLIENT_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_CLIENT_URL_PARAMS = '&idFam=25&idFamCls=59&pag=242&sraccess=1&tmp=758';
const CRM_PHARMACY_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_PHARMACY_URL_PARAMS = '&idFam=3&idFamCls=15&pag=25&sraccess=1&tmp=616';

type IncidenciaFilter = 'todos' | 'sin' | 'con' | 'e4';

interface TransmisionesTabProps {
  selectedRecIds?: number[];
}

const TransmisionesTab: React.FC<TransmisionesTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionTransmision[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionTransmision[]>([]);
  const ESTADO_TODOS = -1;
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [incidenciaFilter, setIncidenciaFilter] = useState<IncidenciaFilter>('todos');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [processing, setProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    total: number;
    current: number;
    currentName: string;
    success: number;
    failed: number;
    cancelled: boolean;
    done: boolean;
  } | null>(null);
  const [batchProcessingId, setBatchProcessingId] = useState<number | null>(null);
  const cancelBatchRef = React.useRef(false);
  const [estadoCounts, setEstadoCounts] = useState({ pendiente: 0, realizada: 0, descartada: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Cambio de estado por lotes (descartar pendientes / revertir descartadas)
  const [batchEstado, setBatchEstado] = useState<number | undefined>(undefined);
  const [batchEstadoLoading, setBatchEstadoLoading] = useState(false);

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionTransmision | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [showTitularesModal, setShowTitularesModal] = useState(false);
  const [relatedIncidencias, setRelatedIncidencias] = useState<ReconciliacionIncidencia[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [procesarResult, setProcesarResult] = useState<ProcesarTransmisionResponse | null>(null);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getRecTransmisiones(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getRecTransmisiones(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const [pendientes, realizadas, descartadas] = await Promise.all([
        fetchByEstado(ESTADO_PENDIENTE),
        fetchByEstado(ESTADO_REALIZADO),
        fetchByEstado(ESTADO_DESCARTADO),
      ]);
      setEstadoCounts({ pendiente: pendientes.length, realizada: realizadas.length, descartada: descartadas.length });
    } catch {
      // silently fail
    } finally {
      setLoadingCounts(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applyFilters(result, searchText, incidenciaFilter);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    setSelectedRowKeys([]);
  }, [selectedEstado, selectedRecIds]);

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  const hasE4 = (t: ReconciliacionTransmision) =>
    t.vendedores?.some(v => v.resultado === -1 && v.resultadoNotas?.startsWith('[E4]')) ||
    t.compradores?.some(c => c.resultado === -1 && c.resultadoNotas?.startsWith('[E4]'));

  const applyFilters = (items: ReconciliacionTransmision[], text: string, incFilter: IncidenciaFilter) => {
    let result = items;

    // Filtro incidencia
    if (incFilter === 'sin') result = result.filter(t => !t.tieneExcepciones);
    else if (incFilter === 'con') result = result.filter(t => t.tieneExcepciones);
    else if (incFilter === 'e4') result = result.filter(hasE4);

    // Filtro búsqueda
    if (text.trim()) {
      const lower = text.toLowerCase();
      result = result.filter(t =>
        (t.farmacia?._Name || '').toLowerCase().includes(lower) ||
        t.patron.toLowerCase().includes(lower) ||
        (t.notasGestion || '').toLowerCase().includes(lower)
      );
    }
    setFilteredData(result);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters(data, value, incidenciaFilter);
  };

  const handleIncidenciaChange = (value: IncidenciaFilter) => {
    setIncidenciaFilter(value);
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
    applyFilters(data, searchText, value);
  };

  const sinIncidenciaPendientes = data.filter(t => t.estadoId === ESTADO_PENDIENTE && !t.tieneExcepciones);

  const processBatch = async (items: ReconciliacionTransmision[], forzarTitularidad = false) => {
    setProcessing(true);
    cancelBatchRef.current = false;
    setBatchProgress({ total: items.length, current: 0, currentName: '', success: 0, failed: 0, cancelled: false, done: false });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < items.length; i++) {
      if (cancelBatchRef.current) {
        setBatchProgress(prev => prev ? { ...prev, cancelled: true, done: true } : null);
        break;
      }

      const item = items[i];
      const farmaciaName = item.farmacia?._Name || `ID ${item.farmaciaId}`;
      setBatchProcessingId(item.id);
      setBatchProgress(prev => prev ? { ...prev, current: i + 0.5, currentName: farmaciaName } : null);

      try {
        const result = await procesarTransmision(item.id, forzarTitularidad);
        if (result.error !== 0) {
          failed++;
        } else {
          success++;
        }
        try {
          const refreshed = await getRecTransmisionById(item.id);
          setData(prev => prev.map(t => t.id === item.id ? refreshed : t));
          setFilteredData(prev => prev.map(t => t.id === item.id ? refreshed : t));
        } catch { /* keep going */ }
      } catch {
        failed++;
      }

      setBatchProgress(prev => prev ? { ...prev, current: i + 1, success, failed } : null);
    }

    setBatchProcessingId(null);
    setBatchProgress(prev => prev ? { ...prev, done: true, success, failed } : null);
    setProcessing(false);
    setSelectedRowKeys([]);
    loadCounts();
  };

  const handleCancelBatch = () => {
    cancelBatchRef.current = true;
  };

  const handleCloseBatchProgress = () => {
    setBatchProgress(null);
  };

  const handleProcesarTodos = () => {
    const items = sinIncidenciaPendientes;
    Modal.confirm({
      title: 'Actualizar Gescom',
      content: `Se actualizarán las titularidades de ${items.length} transmisiones sin bloqueo. Esta acción no se puede deshacer. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items); },
    });
  };

  const handleProcesarSeleccionados = () => {
    const items = data.filter(t => selectedRowKeys.includes(t.id));
    Modal.confirm({
      title: 'Actualizar Gescom',
      content: `Se actualizarán las titularidades de ${items.length} transmisiones seleccionadas. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items); },
    });
  };

  const handleProcesarE4Seleccionados = () => {
    const items = data.filter(t => selectedRowKeys.includes(t.id));
    Modal.confirm({
      title: 'Procesar con bloqueo E4',
      content: `Se procesarán ${items.length} transmisiones forzando la titularidad. La titularidad previa de los titulares entrantes se dará por finalizada. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: () => { processBatch(items, true); },
    });
  };

  const doBatchEstado = async () => {
    setBatchEstadoLoading(true);
    try {
      await Promise.all(selectedRowKeys.map(id => updateRecTransmisionEstado(Number(id), batchEstado!)));
      message.success(`${selectedRowKeys.length} transmisiones actualizadas`);
      setSelectedRowKeys([]);
      setBatchEstado(undefined);
      await Promise.all([loadData(), loadCounts()]);
    } catch {
      message.error('Error al actualizar las transmisiones en lote');
    } finally {
      setBatchEstadoLoading(false);
    }
  };

  const handleBatchEstado = () => {
    if (batchEstado === undefined || selectedRowKeys.length === 0) return;
    const label = selectedEstado === ESTADO_PENDIENTE ? 'Descartada' : 'Pendiente';
    Modal.confirm({
      title: 'Confirmar cambio de estado',
      content: `Se marcarán ${selectedRowKeys.length} transmisiones como "${label}". ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: doBatchEstado,
    });
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    try {
      await updateRecTransmisionEstado(id, estadoId, notasGestion);
      message.success('Transmisión actualizada correctamente');
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar la transmisión');
    }
  };

  const openDetail = async (item: ReconciliacionTransmision) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setRelatedIncidencias([]);
    setProcesarResult(null);
    setModalVisible(true);

    if (item.tieneExcepciones) {
      try {
        const incidencias = await getIncidencias(item.reconciliacionId);
        const related = incidencias.filter(i =>
          i.farmaciaId === item.farmaciaId && (i.tipo === 'E1' || i.tipo === 'E2' || i.tipo === 'I1')
        );
        setRelatedIncidencias(related);
      } catch {
        // silently fail
      }
    }
  };

  const columns: ColumnsType<ReconciliacionTransmision> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      render: (_, r) => r.farmacia?._Name || `ID ${r.farmaciaId}`,
      sorter: (a, b) => (a.farmacia?._Name || '').localeCompare(b.farmacia?._Name || ''),
      ellipsis: true,
      width: 280,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 110,
      render: (val: string) => val ? new Date(val).toLocaleDateString('es-ES') : '-',
    },
    {
      title: <Tooltip overlayStyle={{ maxWidth: 500 }} title={<div style={{ whiteSpace: 'pre-line' }}>{'AT = Alta Titular, CT = Cierre Titular\n───────────────────\nF2 = entra algún titular (AT>0), no sale ninguno (CT=0)\nF3 = no entra ningún titular (AT=0), sale alguno (CT>0)\nF4 = hay entrada y salida de titulares (AT>0, CT>0)'}</div>}>Patrón</Tooltip>,
      dataIndex: 'patron',
      key: 'patron',
      width: 80,
      render: (val: string) => {
        const desc: Record<string, string> = {
          F2: 'Entra algún titular, no sale ninguno',
          F3: 'No entra ningún titular, sale alguno',
          F4: 'Hay entrada y salida de titulares',
        };
        return <Tooltip title={desc[val] || val}><Tag>{val}</Tag></Tooltip>;
      },
    },
    {
      title: <Tooltip title="Cierre (baja) de titular">CT</Tooltip>,
      key: 'vendedores',
      width: 50,
      align: 'center',
      render: (_, r) => r.vendedores?.length || 0,
    },
    {
      title: <Tooltip title="Alta de titular">AT</Tooltip>,
      key: 'compradores',
      width: 50,
      align: 'center',
      render: (_, r) => r.compradores?.length || 0,
    },
    {
      title: <Tooltip overlayStyle={{ maxWidth: 650 }} title={<div style={{ whiteSpace: 'pre-line' }}>{'Bloqueos que requieren supervisión\n───────────────────\nE1 = Alta de titularidad bloqueada porque su finalización se ha verificado recientemente\nE2 = Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente\nI1 = La farmacia quedaría sin titular con estos cambios\n───────────────────\nNormalmente se trata de transmisiones notificadas por los asesores y/o verificadas por otros medios'}</div>}>Bloqueos</Tooltip>,
      key: 'bloqueos',
      width: 70,
      render: (_, r) => {
        const tags: React.ReactNode[] = [];
        // E1/E2 del motor
        r.vendedores?.forEach(v => {
          if (v.excepcionE2) tags.push(
            <Tooltip key={`E2-${v.vendedorId}`} title={INCIDENCIA_TOOLTIPS.E2}>
              <Tag color={INCIDENCIA_COLORS.E2}>E2</Tag>
            </Tooltip>
          );
        });
        r.compradores?.forEach(c => {
          if (c.excepcionE1) tags.push(
            <Tooltip key={`E1-${c.compradorId}`} title={INCIDENCIA_TOOLTIPS.E1}>
              <Tag color={INCIDENCIA_COLORS.E1}>E1</Tag>
            </Tooltip>
          );
        });
        // I1 (invariante)
        if (r.tieneIncidenciaI1) tags.push(
          <Tooltip key="I1" title={INCIDENCIA_TOOLTIPS.I1}>
            <Tag color={INCIDENCIA_COLORS.I1}>I1</Tag>
          </Tooltip>
        );
        // Bloqueos de procesamiento (E3, E4, etc.) — extraer tipo del prefijo [XX] en resultadoNotas
        const procesados = [
          ...(r.vendedores?.filter(v => v.resultado === -1 && v.resultadoNotas) || []).map(v => v.resultadoNotas!),
          ...(r.compradores?.filter(c => c.resultado === -1 && c.resultadoNotas) || []).map(c => c.resultadoNotas!),
        ];
        const tiposVistos = new Set<string>();
        procesados.forEach(notas => {
          const match = notas.match(/^\[([A-Z0-9]+)\]/);
          if (match && !tiposVistos.has(match[1])) {
            tiposVistos.add(match[1]);
            tags.push(
              <Tooltip key={`proc-${match[1]}`} title={notas.replace(/^\[[A-Z0-9]+\]\s*/, '')}>
                <Tag color="volcano">{match[1]}</Tag>
              </Tooltip>
            );
          }
        });
        return tags.length > 0 ? tags : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 70,
      render: (val: number, record: ReconciliacionTransmision) => {
        if (batchProcessingId === record.id) {
          return <Spin size="small" />;
        }
        const estados: Record<number, { label: string; color: string }> = {
          [ESTADO_PENDIENTE]: { label: 'Pendiente', color: 'orange' },
          [ESTADO_REALIZADO]: { label: 'Realizada', color: 'green' },
          [ESTADO_DESCARTADO]: { label: 'Descartada', color: 'default' },
        };
        const est = estados[val] || { label: `Estado ${val}`, color: 'default' };
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
    {
      title: 'Info',
      key: 'info',
      width: 120,
      ellipsis: true,
      render: (_, r) => r.infoProcesamiento ? (
        <Tooltip title={r.infoProcesamiento}><Text type="secondary" style={{ fontSize: 12 }}>{r.infoProcesamiento}</Text></Tooltip>
      ) : null,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  // Selección habilitada en vistas de un único estado (Pendiente / Descartada),
  // salvo durante un procesamiento por lotes en curso.
  const selectionEnabled = !processing && (selectedEstado === ESTADO_PENDIENTE || selectedEstado === ESTADO_DESCARTADO);

  // Opciones del selector de cambio de estado por lotes (por ahora, una única opción por vista).
  const batchTargetOptions = selectedEstado === ESTADO_PENDIENTE
    ? [{ value: ESTADO_DESCARTADO, label: <span style={{ color: '#8c8c8c' }}>● Descartada</span> }]
    : [{ value: ESTADO_PENDIENTE, label: <span style={{ color: '#fa8c16' }}>● Pendiente</span> }];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar transmisiones"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
    setSearchText('');
    setIncidenciaFilter('todos');
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
  };

  const handleRefresh = async () => {
    await Promise.all([loadData(), loadCounts()]);
  };

  return (
    <div>
      <style>{`
        .batch-active-row > td {
          background-color: #e6f7ff !important;
        }
      `}</style>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <Select
          value={incidenciaFilter}
          onChange={handleIncidenciaChange}
          style={{ width: 180 }}
          size="large"
          options={[
            { label: 'Todos', value: 'todos' },
            { label: 'Sin bloqueo', value: 'sin' },
            { label: 'Con bloqueo', value: 'con' },
            { label: 'Con bloqueo E4', value: 'e4' },
          ]}
        />
        {selectedEstado === ESTADO_PENDIENTE && incidenciaFilter === 'sin' && (
          <>
            <Button
              type="primary"
              onClick={handleProcesarTodos}
              disabled={sinIncidenciaPendientes.length === 0 || processing}
              loading={processing}
            >
              Procesar todos sin bloqueo ({sinIncidenciaPendientes.length})
            </Button>
            <Button
              onClick={handleProcesarSeleccionados}
              disabled={selectedRowKeys.length === 0 || processing}
              loading={processing}
            >
              Procesar seleccionados ({selectedRowKeys.length})
            </Button>
          </>
        )}
        {selectedEstado === ESTADO_PENDIENTE && incidenciaFilter === 'e4' && (
          <Button
            onClick={handleProcesarE4Seleccionados}
            disabled={selectedRowKeys.length === 0 || processing}
            loading={processing}
          >
            Procesar con bloqueo E4 seleccionados ({selectedRowKeys.length})
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Buscar farmacia..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={estadoCounts.pendiente + estadoCounts.realizada + estadoCounts.descartada}
                    style={{ backgroundColor: '#595959' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Pendiente</span>
                  <Badge
                    count={estadoCounts.pendiente}
                    style={{ backgroundColor: '#fa8c16' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_PENDIENTE,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Realizada</span>
                  <Badge
                    count={estadoCounts.realizada}
                    style={{ backgroundColor: '#52c41a' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_REALIZADO,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Descartada</span>
                  <Badge
                    count={estadoCounts.descartada}
                    style={{ backgroundColor: '#8c8c8c' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_DESCARTADO,
            },
          ]}
          size="large"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          Actualizar
        </Button>
      </div>

      {/* Barra de cambio de estado por lotes (descartar pendientes / revertir descartadas) */}
      {selectionEnabled && selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: 12, padding: '10px 16px',
          background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 6,
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Text strong>{selectedRowKeys.length} seleccionados</Text>
          <Select
            placeholder="Marcar como..."
            value={batchEstado}
            onChange={setBatchEstado}
            style={{ width: 200 }}
            options={batchTargetOptions}
          />
          <Button type="primary" disabled={batchEstado === undefined} loading={batchEstadoLoading} onClick={handleBatchEstado}>
            Aceptar
          </Button>
          <Button onClick={() => { setSelectedRowKeys([]); setBatchEstado(undefined); }}>Cancelar</Button>
        </div>
      )}

      {/* Barra de progreso del procesamiento por lotes */}
      {batchProgress && (
        <div style={{
          marginBottom: 12,
          padding: '12px 16px',
          background: batchProgress.done
            ? (batchProgress.failed > 0 ? '#fff7e6' : '#f6ffed')
            : '#e6f7ff',
          border: `1px solid ${batchProgress.done ? (batchProgress.failed > 0 ? '#ffd591' : '#b7eb8f') : '#91d5ff'}`,
          borderRadius: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Space>
              {!batchProgress.done && <Spin size="small" />}
              <Text strong>
                {batchProgress.done
                  ? (batchProgress.cancelled
                    ? `Procesamiento cancelado — ${batchProgress.current - 1} de ${batchProgress.total}`
                    : `Procesamiento completado — ${batchProgress.total} transmisiones`)
                  : `Procesando ${Math.ceil(batchProgress.current)} de ${batchProgress.total}...`}
              </Text>
            </Space>
            {batchProgress.done ? (
              <Button size="small" onClick={handleCloseBatchProgress}>Cerrar</Button>
            ) : (
              <Button size="small" icon={<StopOutlined />} onClick={handleCancelBatch} danger>Cancelar</Button>
            )}
          </div>
          <Progress
            percent={Math.round((batchProgress.current / batchProgress.total) * 100)}
            status={batchProgress.done ? (batchProgress.failed > 0 ? 'exception' : 'success') : 'active'}
            size="small"
          />
          <div style={{ marginTop: 4, display: 'flex', gap: 16 }}>
            {!batchProgress.done && <Text type="secondary" style={{ fontSize: 12 }}>{batchProgress.currentName}</Text>}
            <Text type="success" style={{ fontSize: 12 }}>{batchProgress.success} OK</Text>
            {batchProgress.failed > 0 && <Text type="danger" style={{ fontSize: 12 }}>{batchProgress.failed} con error</Text>}
          </div>
        </div>
      )}

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(t => ({ ...t, key: t.id }))}
        columns={columns}
        rowSelection={selectionEnabled ? rowSelection : undefined}
        loading={loading}
        pagination={processing ? false : { defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        rowClassName={(record) => batchProcessingId === record.id ? 'batch-active-row' : ''}
        onRow={record => ({
          onClick: processing ? undefined : () => openDetail(record),
          style: {
            cursor: processing ? 'default' : 'pointer',
          },
        })}
        size="middle"
        scroll={{ x: 900 }}
        locale={{ emptyText: <Empty description="No hay transmisiones pendientes" /> }}
      />

      {/* Modal de detalle */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? <span><Tag>{selectedItem.patron}</Tag> {selectedItem.farmacia?._Name || 'Farmacia ' + selectedItem.farmaciaId}</span> : ''}
        width="60%"
        style={{ top: 20 }}
        styles={{ body: { padding: '24px', background: '#fafafa' } }}
      >
        {selectedItem && (
          <div>
            {/* Layout principal: Farmacia (izq) + flechas + CT/AT (der) */}
            <Row gutter={[0, 16]} style={{ marginBottom: 24 }}>

              {/* Card Farmacia */}
              <Col xs={24} lg={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'gs',
                              label: <span>ID Gescom: <strong>{selectedItem.farmaciaId}</strong> <CopyOutlined style={{ color: '#999', marginLeft: 4 }} /></span>,
                              onClick: () => { navigator.clipboard.writeText(String(selectedItem.farmaciaId)); },
                            },
                          ],
                        }}
                        trigger={['click']}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          <ShopOutlined style={{ marginRight: 8 }} />
                          Farmacia
                        </span>
                      </Dropdown>
                      <Tooltip title="Ver titulares actuales en Gescom">
                        <InfoCircleOutlined
                          style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }}
                          onClick={() => setShowTitularesModal(true)}
                        />
                      </Tooltip>
                    </div>
                  }
                  bordered
                  style={{
                    borderColor: '#d9d9d9',
                    background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    height: '100%',
                  }}
                  headStyle={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                >
                  <a
                    href={`${CRM_PHARMACY_URL}${selectedItem.farmaciaId}${CRM_PHARMACY_URL_PARAMS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 'bold', fontSize: 14, display: 'block' }}
                  >
                    {selectedItem.farmacia?._Name || `ID ${selectedItem.farmaciaId}`}
                  </a>
                  <div style={{
                    marginTop: 12,
                    padding: '4px 12px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                    fontSize: '13px',
                    color: '#0050b3',
                    display: 'inline-block',
                  }}>
                    {selectedItem.farmacia?.provincia || '—'}
                  </div>
                </Card>
              </Col>

              {/* Flechas + Cards CT/AT */}
              <Col xs={24} lg={13}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingLeft: 8 }}>

                  {/* CT: flecha + card */}
                  {selectedItem.vendedores?.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title="Salen de la farmacia">
                        <ArrowRightOutlined style={{ fontSize: 18, color: '#cf1322', flexShrink: 0 }} />
                      </Tooltip>
                      <Card
                        title="Titulares salientes (CT)"
                        size="small"
                        bordered
                        style={{
                          flex: 1,
                          borderColor: '#ffa39e',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                        headStyle={{ backgroundColor: '#fff1f0', fontWeight: 'bold', color: '#cf1322' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {selectedItem.vendedores.map((v, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                              <Space size="small">
                                {v.resultado != null && (v.resultado === 1
                                  ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                                  : <CloseCircleFilled style={{ color: '#f5222d' }} />)}
                                <a
                                  href={v.cliente ? `${CRM_CLIENT_URL}${v.cliente.id}${CRM_CLIENT_URL_PARAMS}` : undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {v.cliente?._Name || `ID ${v.vendedorId}`}
                                </a>
                              </Space>
                              <Space size="small">
                                {v.fechaFin && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(v.fechaFin).toLocaleDateString('es-ES')}</Text>}
                                {v.excepcionE2 && <Tooltip title={INCIDENCIA_TOOLTIPS.E2}><Tag color="red">E2</Tag></Tooltip>}
                              </Space>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}

                  {/* AT: flecha + card */}
                  {selectedItem.compradores?.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title="Entran en la farmacia">
                        <ArrowLeftOutlined style={{ fontSize: 18, color: '#389e0d', flexShrink: 0 }} />
                      </Tooltip>
                      <Card
                        title="Titulares entrantes (AT)"
                        size="small"
                        bordered
                        style={{
                          flex: 1,
                          borderColor: '#b7eb8f',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                        headStyle={{ backgroundColor: '#f6ffed', fontWeight: 'bold', color: '#389e0d' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {selectedItem.compradores.map((c, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                              <Space size="small">
                                {c.resultado != null && (c.resultado === 1
                                  ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                                  : <CloseCircleFilled style={{ color: '#f5222d' }} />)}
                                <a
                                  href={c.cliente ? `${CRM_CLIENT_URL}${c.cliente.id}${CRM_CLIENT_URL_PARAMS}` : undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {c.cliente?._Name || `ID ${c.compradorId}`}
                                </a>
                              </Space>
                              <Space size="small">
                                {c.fechaInicio && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(c.fechaInicio).toLocaleDateString('es-ES')}</Text>}
                                {c.excepcionE1 && <Tooltip title={INCIDENCIA_TOOLTIPS.E1}><Tag color="red">E1</Tag></Tooltip>}
                              </Space>
                            </div>
                          ))}
                        </Space>
                      </Card>
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Comentario (izq) + Bloqueos (der) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {/* Comentario — alineado con card farmacia */}
              <Col xs={24} lg={11}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
                <TextArea
                  value={editNotas}
                  onChange={e => setEditNotas(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  placeholder="Añadir comentario..."
                />
              </Col>

              {/* Bloqueos — alineado con cards titulares */}
              {selectedItem.tieneExcepciones && (
                <Col xs={24} lg={13}>
                  {relatedIncidencias.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 8 }}>
                      {relatedIncidencias.map((inc) => (
                        <Card
                          key={inc.id}
                          size="small"
                          bordered
                          style={{ borderColor: inc.tipo === 'I1' ? '#d3adf7' : '#ffa39e', background: inc.tipo === 'I1' ? '#f9f0ff' : '#fff2f0' }}
                        >
                          <div style={{ display: 'flex', gap: 12 }}>
                            <Tag color={INCIDENCIA_COLORS[inc.tipo] || 'red'} style={{ flexShrink: 0, height: 'fit-content', fontSize: 14, padding: '2px 10px' }}>{inc.tipo}</Tag>
                            <div>
                              <Text strong style={{ display: 'block', color: inc.tipo === 'I1' ? '#722ed1' : '#cf1322' }}>
                                {inc.tipo === 'E1'
                                  ? 'Alta de titularidad bloqueada porque su finalización se ha verificado recientemente'
                                  : inc.tipo === 'E2'
                                  ? 'Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente'
                                  : 'La farmacia quedaría sin titular con estos cambios'}
                              </Text>
                              {inc.descripcion && (
                                <Text style={{ display: 'block', marginTop: 4 }}>{inc.descripcion}</Text>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div style={{ paddingLeft: 8 }}>
                      {selectedItem.vendedores?.filter(v => v.excepcionE2).map((v, i) => (
                        <Card key={`e2-${i}`} size="small" bordered style={{ borderColor: '#ffa39e', background: '#fff2f0', marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <Tag color="red" style={{ flexShrink: 0, height: 'fit-content', fontSize: 14, padding: '2px 10px' }}>E2</Tag>
                            <Text strong style={{ color: '#cf1322' }}>Cierre de titularidad bloqueado porque la titularidad se ha verificado recientemente</Text>
                          </div>
                        </Card>
                      ))}
                      {selectedItem.compradores?.filter(c => c.excepcionE1).map((c, i) => (
                        <Card key={`e1-${i}`} size="small" bordered style={{ borderColor: '#ffa39e', background: '#fff2f0', marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <Tag color="red" style={{ flexShrink: 0, height: 'fit-content', fontSize: 14, padding: '2px 10px' }}>E1</Tag>
                            <Text strong style={{ color: '#cf1322' }}>Alta de titularidad bloqueada porque su finalización se ha verificado recientemente</Text>
                          </div>
                        </Card>
                      ))}
                      {selectedItem.tieneIncidenciaI1 && (
                        <Card key="i1" size="small" bordered style={{ borderColor: '#d3adf7', background: '#f9f0ff', marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <Tag color="purple" style={{ flexShrink: 0, height: 'fit-content', fontSize: 14, padding: '2px 10px' }}>I1</Tag>
                            <Text strong style={{ color: '#722ed1' }}>La farmacia quedaría sin titular con estos cambios</Text>
                          </div>
                        </Card>
                      )}
                    </div>
                  )}
                </Col>
              )}
            </Row>

            {/* Panel de resultado de procesamiento */}
            {(procesarResult || selectedItem.notasProcesamiento) && (
              <Collapse
                size="small"
                defaultActiveKey={procesarResult ? ['1'] : []}
                style={{ marginBottom: 16 }}
                items={[{
                  key: '1',
                  label: <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span><ThunderboltOutlined style={{ marginRight: 8 }} />Resultado del procesamiento</span>{selectedItem.fechaProcesamiento && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(selectedItem.fechaProcesamiento).toLocaleDateString('es-ES')}</Text>}</div>,
                  children: (
                    <div>
                      {/* Estado general */}
                      <div style={{ marginBottom: 8 }}>
                        {procesarResult?.tipoIncidencia && <Tag color="red" style={{ marginRight: 8 }}>{procesarResult.tipoIncidencia}</Tag>}
                        <Text strong>
                          {selectedItem.notasProcesamiento || (procesarResult ? `Caso: ${procesarResult.resultadoCasoLimpio}` : '')}
                        </Text>
                      </div>

                      {/* Detalle por titular (de los datos guardados en la entidad) */}
                      {selectedItem.vendedores?.filter(v => v.resultado != null).length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Titulares salientes:</Text>
                          {selectedItem.vendedores.filter(v => v.resultado != null).map((v, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
                              {v.resultado === 1
                                ? <CheckCircleFilled style={{ color: '#52c41a', marginTop: 4 }} />
                                : <CloseCircleFilled style={{ color: '#f5222d', marginTop: 4 }} />}
                              <div>
                                <Text>{v.cliente?._Name || `ID ${v.vendedorId}`}</Text>
                                {v.resultadoNotas && <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.resultadoNotas}</Text>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedItem.compradores?.filter(c => c.resultado != null).length > 0 && (
                        <div>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Titulares entrantes:</Text>
                          {selectedItem.compradores.filter(c => c.resultado != null).map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 4 }}>
                              {c.resultado === 1
                                ? <CheckCircleFilled style={{ color: '#52c41a', marginTop: 4 }} />
                                : <CloseCircleFilled style={{ color: '#f5222d', marginTop: 4 }} />}
                              <div>
                                <Text>{c.cliente?._Name || `ID ${c.compradorId}`}</Text>
                                {c.resultadoNotas && <Text type="secondary" style={{ display: 'block', fontSize: 12, whiteSpace: 'pre-wrap' }}>{c.resultadoNotas}</Text>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Info (notasTramitacion) */}
                      {(selectedItem.infoProcesamiento || procesarResult?.notasTramitacion) && (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Info: </Text>
                          <Text style={{ fontSize: 12 }}>{selectedItem.infoProcesamiento || procesarResult?.notasTramitacion}</Text>
                        </div>
                      )}
                    </div>
                  ),
                }]}
              />
            )}

            {/* Acciones: estado selector + Procesar + Guardar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 160 }}
              >
                <Select.Option value={ESTADO_PENDIENTE}><span style={{ color: '#fa8c16' }}>● Pendiente</span></Select.Option>
                <Select.Option value={ESTADO_REALIZADO}><span style={{ color: '#52c41a' }}>● Realizada</span></Select.Option>
                <Select.Option value={ESTADO_DESCARTADO}><span style={{ color: '#8c8c8c' }}>● Descartada</span></Select.Option>
              </Select>

              <Button
                icon={<SaveOutlined />}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
              >
                Guardar
              </Button>

              {selectedItem.estadoId === ESTADO_PENDIENTE && (
                <Tooltip title={selectedItem.tieneExcepciones ? 'Los casos con bloqueo requieren revisión antes de procesar' : undefined}>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={procesando}
                  disabled={selectedItem.tieneExcepciones}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Actualizar Gescom',
                      content: 'Se actualizarán las titularidades en Gescom (titulares entrantes y salientes).',
                      okText: 'Aceptar',
                      cancelText: 'Cancelar',
                      onOk: async () => {
                        setProcesando(true);
                        try {
                          const result = await procesarTransmision(selectedItem.id);
                          setProcesarResult(result);

                          if (result.error !== 0) {
                            message.error(`Error: ${result.mensajeError}`);
                          } else if (result.resultadoCasoLimpio !== 'limpio') {
                            message.warning(`Caso no limpio: ${result.resultadoCasoLimpio}`);
                          } else {
                            message.success('Transmisión procesada correctamente');
                            setEditEstadoId(ESTADO_REALIZADO);
                          }

                          const refreshed = await getRecTransmisionById(selectedItem.id);
                          setSelectedItem(refreshed);
                          setEditEstadoId(refreshed.estadoId);
                        } catch {
                          message.error('Error al comunicar con Gescom');
                        } finally {
                          setProcesando(false);
                        }
                      },
                    });
                  }}
                >
                  Procesar...
                </Button>
                </Tooltip>
              )}

              <div style={{ marginLeft: 'auto' }}>
                <AbrirCasoButton
                  prefill={{
                    entidadTipo: 'Transmision',
                    entidadRefId: selectedItem.id,
                    reconciliacionId: selectedItem.reconciliacionId,
                    titulo: `Revisión transmisión #${selectedItem.id}`,
                    descripcion:
                      `Farmacia: ${selectedItem.farmacia?._Name ?? '—'}\n` +
                      `id_farmacia (CU2): ${selectedItem.farmacia?.idFarmaciaCU2 ?? '—'}\n` +
                      `id_farmacia_gs (Gescom): ${selectedItem.farmaciaId}`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal titulares actuales en Gescom */}
      {selectedItem && (
        <FarmaciaTitularesModal
          visible={showTitularesModal}
          onClose={() => setShowTitularesModal(false)}
          farmaciaId={selectedItem.farmaciaId}
          farmaciaName={selectedItem.farmacia?._Name || 'Sin datos'}
          userId={1}
        />
      )}
    </div>
  );
};

export default TransmisionesTab;
